import React, { useState, useEffect } from "react";
import { SearchIcon, Settings, RotateCw, X, FilterX, Plus } from "lucide-react";
import { IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ProductFilters from "./Components/AllProductsComponents/ProductFilters";
import ProductTable, {
  defaultColumns,
} from "./Components/AllProductsComponents/ProductTable";
import { errorToast, successToast } from "../../../services/toast.service";
import BreadCrumbsContainer from "../../Common/Breadcrumbs";
import { useFetchAllVendorProductsQuery } from "../../../services/baseQuery";

const AllProducts = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(
    defaultColumns.reduce((acc, col) => ({ ...acc, [col.id]: true }), {}),
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refetchFn, setRefetchFn] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState({
    brand: "",
    category: "",
    status: "",
    sortBy: "",
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500); // Wait 500ms after last keystroke

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSettingsClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSettings = () => {
    setAnchorEl(null);
  };

  const handleColumnToggle = columnId => {
    const column = defaultColumns.find(col => col.id === columnId);
    if (column?.required) return;

    setVisibleColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  // Create the refresh handler
  const handleRefresh = async () => {
    if (refetchFn) {
      setIsRefreshing(true);
      try {
        await refetchFn();
        successToast("Product list refreshed");
      } catch (error) {
        errorToast("Failed to refresh product list");
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  const handleSearchChange = event => {
    setSearchQuery(event.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const handleFilterChange = newFilters => {
    setFilters(newFilters);
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setFilters({
      brand: "",
      category: "",
      status: "",
      sortBy: "",
    });
  };

  return (
    <div className="h-screen overflow-hidden">
      <BreadCrumbsContainer />
      <div className="bg-white h-[calc(100vh-120px)]">
        {/* Header Section */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-semibold font-outfit text-gray-800">
              All Products
            </h1>
            <button
              onClick={() => navigate("/products/addproduct")}
              className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] flex items-center gap-2">
              <Plus size={16} />
              Add Product
            </button>
          </div>

          {/* Search and Settings Section */}
          <div className="flex items-center gap-4">
            {/* Search Bar */}
            <div className="w-[550px]">
              <div className="relative">
                <SearchIcon
                  className="absolute left-3 top-1/2 transform -translate-y-1/2"
                  size={18}
                  color="#83899F"
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search by name, ID, brand..."
                  className="w-full h-10 pl-10 pr-10 rounded-lg border border-gray-200 
                           focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] 
                           outline-none transition-all text-gray-600"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:bg-gray-100 p-1 rounded-full">
                    <X size={16} color="#83899F" />
                  </button>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <IconButton
                onClick={handleRefresh}
                className="hover:bg-gray-100"
                disabled={isRefreshing}
                title="Refresh">
                <RotateCw
                  size={20}
                  className={`text-gray-600 ${
                    isRefreshing ? "animate-spin" : ""
                  }`}
                />
              </IconButton>
              <IconButton
                onClick={handleSettingsClick}
                className="hover:bg-gray-100"
                title="Column Settings">
                <Settings size={20} className="text-gray-600" />
              </IconButton>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <ProductFilters
              onFilterChange={handleFilterChange}
              filters={filters}
            />
            {(searchQuery ||
              Object.values(filters).some(value => value !== "")) && (
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 
                         hover:bg-gray-100 rounded-lg transition-colors">
                <FilterX size={16} />
                Clear Filters
              </button>
            )}
          </div>
          <ProductTable
            onSettingsClick={handleSettingsClick}
            anchorEl={anchorEl}
            onCloseSettings={handleCloseSettings}
            visibleColumns={visibleColumns}
            onColumnToggle={handleColumnToggle}
            setRefetchFn={setRefetchFn}
            searchQuery={debouncedSearch}
            filters={filters}
          />
        </div>
      </div>
    </div>
  );
};

export default AllProducts;
